/** @jsxImportSource theme-ui @jsxRuntime classic */
import React, { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { FocusScope } from '@hzdg/focus-scope';
import { getDocument } from '@hzdg/dom-utils';

import {
  AnimationState,
  useAnimationStateSelector,
  usePageTransitionActions,
  usePageTransitionDispatch,
} from './PageTransition';

import { Overlay } from '.';

import type { PointerEvent } from 'react';
import type { FocusManager } from '@hzdg/focus-scope';
import type { ModalProps } from './Modal';

function shouldHandleEvent(event: PointerEvent, currentTarget: Element | null) {
  if (!currentTarget) return false;
  // If the event target is not the current target, ignore the event.
  if (event.target !== currentTarget) return false;
  // If the event's default behavior is prevented, ignore the event.
  if (event.defaultPrevented) return false;
  // If an auxillary mouse button was pressed, ignore the event.
  if (event.button > 0) return false;
  // If the event target is not attached to a DOM, ignore the event.
  const doc = getDocument(event.target as Node)?.documentElement;
  if (!doc || !doc.contains(event.target as Node)) return false;
  return true;
}

export const ModalInner = ({
  isOpen,
  onClose,
  onCloseFinished,
  onKeyPress,
  onPointerDown,
  onPointerUp,
  trap = true,
  autoFocus = true,
  restoreFocus = true,
  children,
  ...props
}: ModalProps): React.JSX.Element | null => {
  const actions = usePageTransitionActions();
  const dispatch = usePageTransitionDispatch();

  const onCloseFinished_ = useRef(onCloseFinished);
  useEffect(() => {
    onCloseFinished_.current = onCloseFinished;
  }, [onCloseFinished]);

  const ref = useRef<HTMLDivElement>(null);

  const handleKeyPress = useCallback(
    (event: KeyboardEvent, focusManager: FocusManager) => {
      onKeyPress?.(event, focusManager);
      if (event.defaultPrevented) return;
      if (event.key === 'Escape') {
        onClose?.();
        event.preventDefault();
      }
    },
    [onClose, onKeyPress],
  );

  const isDown = useRef(false);
  const handlePointerDown = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      onPointerDown?.(event);
      if (shouldHandleEvent(event, ref.current)) {
        isDown.current = true;
      }
    },
    [onPointerDown],
  );

  const handlePointerUp = useCallback(
    (event: PointerEvent<HTMLDivElement>) => {
      onPointerUp?.(event);
      if (isDown.current && shouldHandleEvent(event, ref.current)) {
        onClose?.();
        event.preventDefault();
      }
      isDown.current = false;
    },
    [onClose, onPointerUp],
  );

  const animationState = useAnimationStateSelector();
  useEffect(() => {
    if (animationState === AnimationState.DID_LEAVE)
      onCloseFinished_.current?.();
  }, [animationState]);

  useLayoutEffect(() => {
    if (
      isOpen &&
      ![
        AnimationState.DID_ENTER,
        AnimationState.IS_ENTERING,
        AnimationState.SHOULD_ENTER,
      ].includes(animationState)
    )
      dispatch(actions.setAll(AnimationState.SHOULD_ENTER));
    else if (
      !isOpen &&
      ![
        AnimationState.DID_LEAVE,
        AnimationState.IS_LEAVING,
        AnimationState.IS_PENDING,
        AnimationState.SHOULD_LEAVE,
      ].includes(animationState)
    ) {
      dispatch(actions.setAll(AnimationState.SHOULD_LEAVE));
    }
  }, [actions, animationState, dispatch, isOpen]);

  const shouldShow =
    isOpen ||
    ![AnimationState.DID_LEAVE, AnimationState.IS_PENDING].includes(
      animationState,
    );

  return shouldShow ? (
    <FocusScope
      {...props}
      as={Overlay}
      // eslint-disable-next-line jsx-a11y/no-autofocus
      autoFocus={autoFocus}
      key="modal"
      modalId={props.id ?? 'modal'}
      onKeyPress={handleKeyPress}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      ref={ref}
      restoreFocus={restoreFocus}
      trap={trap}
    >
      {children}
    </FocusScope>
  ) : null;
};
